import React, { useEffect, useRef, useState } from "react";
import { Search, RotateCcw, Calendar } from "lucide-react";
import { DATE_PRESETS, fmtDate } from "../utils";

// Filters bar — single search input, date-preset chips + custom-range
// popover, and a reset button. Search is debounced (300ms) so typing
// doesn't fire a roundtrip per keystroke.
//
// Layout:
//   [ 🔍 search input ............] [ Today | 7d | 30d | … ] [ 📅 custom ] [ Reset ]

const PRESET_KEY_BY_RANGE = (range) => {
  for (const p of DATE_PRESETS) {
    const r = p.range();
    if (r.from === range.from && r.to === range.to) return p.key;
  }
  return "custom";
};

// Placeholder reflects what the CURRENT view actually searches: roll-up
// levels match store attributes only (and smart-jump the breadcrumb on a
// unique hit); the per-store bill list additionally matches invoice no,
// customer/vendor, phone, and item CODE (prefix). Item-name search was
// removed per product decision — codes are what cashiers key in.
const PLACEHOLDERS = {
  level: "Search store code, city, state, or category…",
  invoices: "Search invoice no, customer/vendor, phone, or item code…",
  detail: "Search invoice no, customer/vendor, phone, or item code…",
};

export default function FiltersBar({ value, onChange, mode = "level" }) {
  const { search, item, from, to } = value;
  const [localSearch, setLocalSearch] = useState(search || "");
  const [showCustom, setShowCustom] = useState(false);
  const debounceRef = useRef(null);
  const customRef = useRef(null);

  // Keep local input in sync if parent resets externally.
  useEffect(() => {
    setLocalSearch(search || "");
  }, [search]);

  // Debounce search input → onChange.
  useEffect(() => {
    if (localSearch === (search || "")) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onChange({ ...value, search: localSearch });
    }, 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localSearch]);

  // Close custom-range popover on outside click.
  useEffect(() => {
    if (!showCustom) return;
    const onDoc = (e) => {
      if (customRef.current && !customRef.current.contains(e.target)) {
        setShowCustom(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [showCustom]);

  const activePreset = PRESET_KEY_BY_RANGE({ from, to });

  const applyPreset = (preset) => {
    const r = preset.range();
    onChange({ ...value, from: r.from, to: r.to });
  };

  const reset = () => {
    const defaultPreset = DATE_PRESETS.find((p) => p.key === "last30");
    const r = defaultPreset.range();
    setLocalSearch("");
    onChange({ search: "", item: "", from: r.from, to: r.to });
  };

  return (
    <div className="bg-white border border-[var(--dashboard-gray-200)] rounded-xl shadow-sm px-4 py-3">
      <div className="flex items-stretch gap-3 flex-wrap">
        {/* Search */}
        <div className="relative flex-1 min-w-[240px]">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--dashboard-gray-400)]"
          />
          <input
            type="text"
            value={localSearch}
            onChange={(e) => setLocalSearch(e.target.value)}
            placeholder={PLACEHOLDERS[mode] || PLACEHOLDERS.level}
            className="w-full pl-9 pr-3 py-2 text-[13.5px] bg-[var(--dashboard-gray-50)] border border-[var(--dashboard-gray-200)] rounded-lg outline-none focus:border-[var(--dashboard-info)] focus:ring-2 focus:ring-[var(--dashboard-info)]/15"
          />
        </div>

        {/* Active item filter — set when a search term wasn't a store scope.
            Everything on screen is narrowed to this product until cleared. */}
        {item ? (
          <div className="flex items-center gap-1.5 px-3 py-2 text-[12.5px] font-semibold rounded-lg bg-[var(--dashboard-bg-success)] text-[var(--dashboard-green-dark)]">
            <span>Item code: {item}</span>
            <button
              type="button"
              onClick={() => onChange({ ...value, item: "" })}
              className="ml-1 font-bold hover:opacity-70"
              title="Clear item filter"
            >
              ✕
            </button>
          </div>
        ) : null}

        {/* Preset chips */}
        <div className="flex items-center gap-1 bg-[var(--nav-head-bg)] rounded-lg p-1">
          {DATE_PRESETS.slice(0, 5).map((p) => {
            const active = activePreset === p.key;
            return (
              <button
                key={p.key}
                type="button"
                onClick={() => applyPreset(p)}
                className={`px-3 py-1.5 text-[12.5px] font-semibold rounded-md transition ${
                  active
                    ? "bg-white text-[var(--dashboard-gray-900)] shadow-sm"
                    : "text-[var(--dashboard-gray-500)] hover:text-[var(--dashboard-gray-900)]"
                }`}
              >
                {p.label}
              </button>
            );
          })}
        </div>

        {/* Custom date popover */}
        <div className="relative" ref={customRef}>
          <button
            type="button"
            onClick={() => setShowCustom((s) => !s)}
            className={`flex items-center gap-2 px-3 py-2 text-[12.5px] font-semibold rounded-lg border transition ${
              activePreset === "custom"
                ? "bg-[var(--dashboard-info)] text-white border-[var(--dashboard-info)]"
                : "bg-white text-[var(--dashboard-gray-900)] border-[var(--dashboard-gray-200)] hover:border-[var(--dashboard-gray-400)]"
            }`}
            title={`${fmtDate(from)} – ${fmtDate(to)}`}
          >
            <Calendar size={14} />
            <span className="hidden md:inline">{fmtDate(from)} – {fmtDate(to)}</span>
            <span className="md:hidden">Custom</span>
          </button>
          {showCustom && (
            <div className="absolute right-0 mt-2 z-20 bg-white border border-[var(--dashboard-gray-200)] rounded-xl shadow-lg p-3 min-w-[260px]">
              <div className="flex flex-col gap-2">
                <label className="text-[11px] font-semibold uppercase tracking-wide text-[var(--dashboard-gray-500)]">
                  From
                </label>
                <input
                  type="date"
                  value={from}
                  max={to || undefined}
                  onChange={(e) => onChange({ ...value, from: e.target.value })}
                  className="px-2 py-1.5 text-[13px] border border-[var(--dashboard-gray-200)] rounded-md outline-none focus:border-[var(--dashboard-info)]"
                />
                <label className="text-[11px] font-semibold uppercase tracking-wide text-[var(--dashboard-gray-500)] mt-1">
                  To
                </label>
                <input
                  type="date"
                  value={to}
                  min={from || undefined}
                  onChange={(e) => onChange({ ...value, to: e.target.value })}
                  className="px-2 py-1.5 text-[13px] border border-[var(--dashboard-gray-200)] rounded-md outline-none focus:border-[var(--dashboard-info)]"
                />
                <div className="grid grid-cols-2 gap-1 mt-2 pt-2 border-t border-[var(--dashboard-gray-100)]">
                  {DATE_PRESETS.slice(5).map((p) => (
                    <button
                      key={p.key}
                      type="button"
                      onClick={() => {
                        applyPreset(p);
                        setShowCustom(false);
                      }}
                      className="px-2 py-1.5 text-[12px] text-left text-[var(--dashboard-gray-500)] hover:bg-[var(--nav-head-bg)] rounded"
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={reset}
          className="flex items-center gap-2 px-3 py-2 text-[12.5px] font-semibold text-[var(--dashboard-gray-500)] bg-white border border-[var(--dashboard-gray-200)] rounded-lg hover:border-[var(--dashboard-gray-400)] hover:text-[var(--dashboard-gray-900)]"
        >
          <RotateCcw size={13} />
          Reset
        </button>
      </div>
    </div>
  );
}
